/**
 * Local Admin Store — development-only interceptor
 * 
 * Prevents admin WRITE operations from hitting the production API.
 * All admin changes are stored in localStorage and reflected locally.
 * READ-only operations (seat availability, public config) still use production.
 */

const IS_DEV = process.env.NODE_ENV === 'development';
const LS_KEY_PRICING = 'local_amp_pricing_overrides';
const LS_KEY_CONFIG = 'local_config_overrides';

// ── Pricing Overrides ──

function getPricingOverrides() {
  try {
    return JSON.parse(localStorage.getItem(LS_KEY_PRICING) || '{}');
  } catch { return {}; }
}

function savePricingOverrides(overrides) {
  localStorage.setItem(LS_KEY_PRICING, JSON.stringify(overrides));
}

/**
 * Intercept updateAmpPricing — save to localStorage instead of production.
 * Accepts same payload as the backend PATCH:
 *   Section-level: { day, section, min, max }
 *   Row-level:     { day, section, row, price }
 *   Clear:         { day, section, [row], clear: true }
 */
function localUpdateAmpPricing(data) {
  const { day, section, row, clear } = data;
  if (!section) return { success: false, error: 'section is required' };

  const overrides = getPricingOverrides();
  const targetDays = day === 'both' ? ['saturday', 'sunday'] : [day];

  for (const d of targetDays) {
    if (d !== 'saturday' && d !== 'sunday') continue;
    if (!overrides[d]) overrides[d] = {};

    if (row) {
      // Per-row override
      if (!overrides[d][section]) overrides[d][section] = {};
      if (!overrides[d][section].rows) overrides[d][section].rows = {};
      if (clear) {
        delete overrides[d][section].rows[row];
        if (Object.keys(overrides[d][section].rows).length === 0) delete overrides[d][section].rows;
        if (Object.keys(overrides[d][section]).length === 0) delete overrides[d][section];
      } else {
        overrides[d][section].rows[row] = parseInt(data.price);
      }
    } else {
      // Section-level override
      if (clear) {
        delete overrides[d][section];
      } else {
        const existingRows = overrides[d][section]?.rows || {};
        overrides[d][section] = { min: parseInt(data.min), max: parseInt(data.max) };
        if (Object.keys(existingRows).length > 0) overrides[d][section].rows = existingRows;
      }
    }
  }

  savePricingOverrides(overrides);
  const label = row ? `row ${row} in ${section}` : section;
  return {
    success: true,
    message: `Price override ${clear ? 'cleared' : 'saved'} for ${label} (${day}) [LOCAL]`,
    data: { overrides },
  };
}

/**
 * Build a full amp pricing response from local JSON files + localStorage overrides.
 * Replicates what the backend AdminAmpPricingView.get() returns.
 */
async function localGetAmpPricing(multiplier = 0.40) {
  const overrides = getPricingOverrides();
  const days = {};

  for (const day of ['saturday', 'sunday']) {
    try {
      const res = await fetch(`/${day}.json`);
      const jsonData = await res.json();
      const dayOverrides = overrides[day] || {};
      days[day] = {
        sections: summarizeSections(jsonData, multiplier, dayOverrides),
        generated_at: jsonData.generatedAt || '',
      };
    } catch (err) {
      days[day] = { error: `${day}.json not found or failed to load` };
    }
  }

  return {
    success: true,
    data: { multiplier, days, overrides },
  };
}

/** Replicate backend _summarize_sections logic in JS */
function summarizeSections(jsonData, multiplier, dayOverrides) {
  const SECTION_ORDER = [
    'Pit (196 Seats)', 'Circle (285 Seats)',
    'Section 1', 'Section 2', 'Section 3',
    'Box Seats',
    'Section 4', 'Section 5', 'Section 6', 'Section 7', 'Section 8',
    'Section 1 ADA', 'Section 3 ADA', 'Section 5 ADA', 'Section 7 ADA',
  ];

  const sectionPrices = {};
  const sectionRows = {};

  for (const seat of (jsonData.seats || [])) {
    const sec = seat.section;
    const price = seat.price;
    const row = seat.row || '?';
    if (!sectionPrices[sec]) sectionPrices[sec] = [];
    sectionPrices[sec].push(price);
    if (!sectionRows[sec]) sectionRows[sec] = {};
    if (!sectionRows[sec][row]) sectionRows[sec][row] = [];
    sectionRows[sec][row].push(price);
  }

  const result = [];
  for (const secName of SECTION_ORDER) {
    const prices = sectionPrices[secName];
    if (!prices || prices.length === 0) continue;

    const originalMin = Math.min(...prices);
    const originalMax = Math.max(...prices);
    const override = dayOverrides[secName] || null;

    let effectiveMin, effectiveMax;
    const applyMult = (price) => multiplier === 1.0 ? parseInt(price) : Math.max(1, Math.round(parseInt(price) * multiplier));
    if (override && (override.min !== undefined || override.max !== undefined)) {
      effectiveMin = override.min != null ? applyMult(override.min) : Math.round(originalMin * multiplier);
      effectiveMax = override.max != null ? applyMult(override.max) : Math.round(originalMax * multiplier);
    } else {
      effectiveMin = Math.round(originalMin * multiplier);
      effectiveMax = Math.round(originalMax * multiplier);
    }

    const rowOverrides = override?.rows || {};
    const rows = [];
    const sortedRowNames = Object.keys(sectionRows[secName] || {}).sort();
    for (const rowName of sortedRowNames) {
      const rowPrices = sectionRows[secName][rowName];
      const rowOrigMin = Math.min(...rowPrices);
      const rowOrigMax = Math.max(...rowPrices);
      const rowOv = rowOverrides[rowName];
      let rowEffMin, rowEffMax;
      if (rowOv != null) {
        rowEffMin = applyMult(rowOv);
        rowEffMax = applyMult(rowOv);
      } else {
        rowEffMin = Math.round(rowOrigMin * multiplier);
        rowEffMax = Math.round(rowOrigMax * multiplier);
      }
      rows.push({
        row: rowName,
        seats: rowPrices.length,
        original_min: rowOrigMin,
        original_max: rowOrigMax,
        effective_min: rowEffMin,
        effective_max: rowEffMax,
        has_override: rowOv != null,
      });
    }

    result.push({
      section: secName,
      seat_count: prices.length,
      original_min: originalMin,
      original_max: originalMax,
      multiplier,
      effective_min: effectiveMin,
      effective_max: effectiveMax,
      has_override: override != null,
      rows,
    });
  }
  return result;
}

// ── Config Overrides (toggles) ──

function getConfigOverrides() {
  try {
    return JSON.parse(localStorage.getItem(LS_KEY_CONFIG) || '{}');
  } catch { return {}; }
}

function saveConfigOverride(key, value) {
  const overrides = getConfigOverrides();
  overrides[key] = value;
  localStorage.setItem(LS_KEY_CONFIG, JSON.stringify(overrides));
}

/**
 * Intercept admin config PATCH — save to localStorage instead of production.
 */
function localUpdateConfig(data) {
  const overrides = getConfigOverrides();
  Object.assign(overrides, data);
  localStorage.setItem(LS_KEY_CONFIG, JSON.stringify(overrides));
  return { success: true, data: overrides, message: 'Config updated [LOCAL]' };
}

/**
 * Merge local config overrides into a production public config response.
 */
function mergeConfigOverrides(prodResponse) {
  const overrides = getConfigOverrides();
  if (!prodResponse?.data || Object.keys(overrides).length === 0) return prodResponse;
  return {
    ...prodResponse,
    data: { ...prodResponse.data, ...overrides },
  };
}

/**
 * Get pricing overrides for a specific day (used by StadiumPage to apply local prices).
 * Returns: { 'Section 1': { rows: { 'A': 120, 'B': 100 }, min: 46, max: 56 }, ... }
 */
function getDayPricingOverrides(day) {
  const overrides = getPricingOverrides();
  return overrides[day] || {};
}

export {
  IS_DEV,
  localUpdateAmpPricing,
  localGetAmpPricing,
  localUpdateConfig,
  mergeConfigOverrides,
  getDayPricingOverrides,
  getPricingOverrides,
  getConfigOverrides,
  saveConfigOverride,
};
